import React, { useState, useEffect, useRef } from 'react';
import { dashboardApi } from '../api/dashboard';
import { formatCurrency } from '../utils/format';
import { Search, X, ShoppingCart, TrendingUp, CreditCard, Banknote, Users, Loader2, Wallet, UserCircle, FileText, ArrowDownUp, Briefcase, Receipt, ArrowLeft } from 'lucide-react';

const FILTER_TABS = [
  { key: 'all', label: 'Semua', icon: Search },
  { key: 'purchase', label: 'Pembelian', icon: ShoppingCart },
  { key: 'sale', label: 'Penjualan', icon: TrendingUp },
  { key: 'debt', label: 'Hutang', icon: CreditCard },
  { key: 'receivable', label: 'Piutang', icon: Banknote },
  { key: 'operational', label: 'Operasional', icon: Briefcase },
  { key: 'owner_expense', label: 'Prive', icon: Wallet },
  { key: 'employee', label: 'Karyawan', icon: UserCircle },
  { key: 'investor', label: 'Investor', icon: Users },
  { key: 'cashflow', label: 'Cashflow', icon: ArrowDownUp },
  { key: 'other_income', label: 'Pendapatan', icon: Receipt },
];

const TYPE_CONFIG = {
  purchase: { color: 'bg-blue-100 text-blue-700', icon: ShoppingCart, label: 'Pembelian' },
  sale: { color: 'bg-emerald-100 text-emerald-700', icon: TrendingUp, label: 'Penjualan' },
  debt: { color: 'bg-red-100 text-red-700', icon: CreditCard, label: 'Hutang' },
  receivable: { color: 'bg-amber-100 text-amber-700', icon: Banknote, label: 'Piutang' },
  operational: { color: 'bg-purple-100 text-purple-700', icon: Briefcase, label: 'Operasional' },
  owner_expense: { color: 'bg-pink-100 text-pink-700', icon: Wallet, label: 'Pengeluaran Owner' },
  investor: { color: 'bg-indigo-100 text-indigo-700', icon: Users, label: 'Investor' },
  employee: { color: 'bg-teal-100 text-teal-700', icon: UserCircle, label: 'Karyawan' },
  cashflow: { color: 'bg-cyan-100 text-cyan-700', icon: ArrowDownUp, label: 'Cashflow' },
  other_income: { color: 'bg-lime-100 text-lime-700', icon: Receipt, label: 'Pendapatan Lain' },
};

// Field labels for detail view
const FIELD_LABELS = {
  supplier: 'Supplier', buyer: 'Buyer', commodity_type: 'Komoditas', quantity_kg: 'Qty (Kg)',
  price_per_kg: 'Harga/Kg', total_amount: 'Total', paid_amount: 'Dibayar', remaining_amount: 'Sisa',
  payment_status: 'Status Bayar', date: 'Tanggal', due_date: 'Jatuh Tempo', location: 'Lokasi',
  invoice_number: 'No. Invoice', entity_id: 'Entitas', creditor_name: 'Kreditur',
  debt_type: 'Tipe Hutang', principal_amount: 'Pokok', outstanding_amount: 'Sisa Hutang',
  status: 'Status', notes: 'Catatan', customer: 'Customer', category: 'Kategori',
  description: 'Deskripsi', amount: 'Jumlah', name: 'Nama', scheme: 'Skema',
  initial_capital: 'Modal Awal', remaining_capital: 'Sisa Modal', profit_share_percent: 'Bagi Hasil %',
  full_name: 'Nama Lengkap', position: 'Jabatan', department: 'Departemen', phone: 'Telepon',
  salary: 'Gaji', join_date: 'Tgl Bergabung', source: 'Sumber',
  broker_fee: 'Fee Broker', fee_jalan: 'Fee Jalan', potongan_spsi: 'Potongan SPSI',
  tax_pph22: 'PPh22', collected_amount: 'Tertagih', received_amount: 'Diterima',
  start_date: 'Tgl Mulai', sp_date: 'Tgl SP',
};

const HIDDEN_FIELDS = ['id', 'entity_id', 'created_at', 'updated_at', 'created_by', 'user_id', 'user_name', 'items', 'fee_details', 'linked_debt_id', 'type'];

function formatValue(key, val) {
  if (val === null || val === undefined || val === '') return '-';
  if (typeof val === 'number') {
    if (key.includes('kg') || key.includes('tonase')) return `${val.toLocaleString('id-ID')} kg`;
    if (key.includes('percent')) return `${val}%`;
    if (val > 1000 || key.includes('amount') || key.includes('price') || key.includes('fee') || key.includes('salary') || key.includes('capital') || key === 'amount') return formatCurrency(val);
    return val.toLocaleString('id-ID');
  }
  if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) return val.slice(0, 10);
  if (typeof val === 'boolean') return val ? 'Ya' : 'Tidak';
  return String(val);
}

function statusColor(status) {
  if (!status || status === '-') return '';
  const s = String(status).toLowerCase();
  if (['paid','collected','active','lunas'].includes(s)) return 'bg-green-100 text-green-700';
  if (['partial','sebagian'].includes(s)) return 'bg-amber-100 text-amber-700';
  if (['unpaid','uncollected','critical','overdue'].includes(s)) return 'bg-red-100 text-red-700';
  return 'bg-stone-100 text-stone-600';
}

export default function GlobalSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setResults([]);
      setActiveFilter('all');
      setSelectedIdx(-1);
      setDetailData(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length >= 2) {
        setLoading(true);
        setSelectedIdx(-1);
        setDetailData(null);
        try {
          const data = await dashboardApi.globalSearch(query, activeFilter);
          setResults(data.results || []);
        } catch (error) {
          setResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
        setDetailData(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, activeFilter]);

  const handleSelect = async (result) => {
    if (!result.id) return;
    setDetailLoading(true);
    try {
      const resp = await dashboardApi.getSearchDetail(result.type, result.id);
      if (resp.data) {
        setDetailData({ ...resp, config: TYPE_CONFIG[result.type] || {} });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBack = () => setDetailData(null);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIdx >= 0 && results[selectedIdx]) {
      e.preventDefault();
      handleSelect(results[selectedIdx]);
    } else if (e.key === 'Escape' && detailData) {
      e.preventDefault();
      handleBack();
    }
  };

  const handleClose = () => {
    onClose();
    setDetailData(null);
    setQuery('');
    setResults([]);
  };

  // Group results by type
  const grouped = {};
  results.forEach(r => {
    if (!grouped[r.type]) grouped[r.type] = [];
    grouped[r.type].push(r);
  });

  let flatIdx = -1;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[8vh] bg-black/40 backdrop-blur-sm" onClick={handleClose}>
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="global-search-modal"
      >
        {/* Detail View */}
        {detailData ? (
          <div data-testid="search-detail-view">
            {/* Detail Header */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-stone-100">
              <button onClick={handleBack} className="p-1 hover:bg-stone-100 rounded-lg" data-testid="detail-back-btn">
                <ArrowLeft size={18} className="text-stone-500" />
              </button>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${detailData.config?.color || 'bg-stone-100 text-stone-600'}`}>
                {detailData.config?.label || detailData.type}
              </span>
              <span className="text-sm font-medium text-stone-700 flex-1">Detail Data</span>
              <button onClick={handleClose} className="p-1 hover:bg-stone-100 rounded-lg" data-testid="detail-close-btn">
                <X size={18} className="text-stone-400" />
              </button>
            </div>

            {/* Detail Content */}
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {detailLoading ? (
                <div className="py-12 text-center">
                  <Loader2 size={24} className="mx-auto mb-2 text-stone-400 animate-spin" />
                  <p className="text-sm text-stone-400">Memuat detail...</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(detailData.data).filter(([key]) => !HIDDEN_FIELDS.includes(key)).map(([key, val]) => {
                    if (typeof val === 'object' && val !== null && !Array.isArray(val)) return null;
                    if (Array.isArray(val)) return null;
                    const label = FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    const formatted = formatValue(key, val);
                    const isStatus = key === 'status' || key === 'payment_status';
                    return (
                      <div key={key} className="flex items-start py-2 border-b border-stone-50">
                        <span className="w-40 text-xs text-stone-400 flex-shrink-0 pt-0.5">{label}</span>
                        {isStatus ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(val)}`}>{formatted}</span>
                        ) : (
                          <span className={`text-sm text-stone-800 ${(key.includes('amount') || key.includes('price') || key.includes('salary') || key.includes('capital') || key === 'amount') ? 'font-mono font-semibold' : ''}`}>
                            {formatted}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Render items array if present (multi-item transactions) */}
                  {detailData.data.items && detailData.data.items.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Item Detail ({detailData.data.items.length})</p>
                      <div className="bg-stone-50 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-stone-200">
                              {Object.keys(detailData.data.items[0]).filter(k => !HIDDEN_FIELDS.includes(k)).slice(0, 6).map(k => (
                                <th key={k} className="px-3 py-1.5 text-left text-stone-500 font-medium">{FIELD_LABELS[k] || k}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.data.items.map((item, i) => (
                              <tr key={i} className="border-b border-stone-100">
                                {Object.entries(item).filter(([k]) => !HIDDEN_FIELDS.includes(k)).slice(0, 6).map(([k, v]) => (
                                  <td key={k} className="px-3 py-1.5 text-stone-700">{formatValue(k, v)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-100">
              <Search size={20} className="text-stone-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Cari nama, supplier, karyawan, deskripsi..."
                className="flex-1 outline-none text-base bg-transparent text-stone-800 placeholder:text-stone-400"
                data-testid="global-search-input"
              />
              {loading && <Loader2 size={18} className="text-stone-400 animate-spin flex-shrink-0" />}
              <kbd className="hidden sm:inline-block text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-400 rounded border border-stone-200">ESC</kbd>
              <button onClick={handleClose} className="p-1 hover:bg-stone-100 rounded-lg transition-colors" data-testid="global-search-close">
                <X size={18} className="text-stone-400" />
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="border-b border-stone-100 px-4 py-2">
              <div className="flex gap-1 overflow-x-auto no-scrollbar">
                {FILTER_TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveFilter(tab.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                        activeFilter === tab.key
                          ? 'bg-[#1A4D2E] text-white'
                          : 'text-stone-500 hover:bg-stone-100'
                      }`}
                      data-testid={`search-filter-${tab.key}`}
                    >
                      <Icon size={13} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results */}
            <div className="max-h-[55vh] overflow-y-auto">
              {query.length < 2 ? (
                <div className="py-12 text-center">
                  <Search size={36} className="mx-auto mb-3 text-stone-300" />
                  <p className="text-sm text-stone-400">Ketik minimal 2 karakter untuk mencari</p>
                  <p className="text-xs text-stone-300 mt-1">Ctrl+K untuk buka pencarian kapan saja</p>
                </div>
              ) : loading ? (
                <div className="py-12 text-center">
                  <Loader2 size={28} className="mx-auto mb-3 text-stone-400 animate-spin" />
                  <p className="text-sm text-stone-400">Mencari...</p>
                </div>
              ) : results.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText size={36} className="mx-auto mb-3 text-stone-300" />
                  <p className="text-sm text-stone-500">Tidak ditemukan hasil untuk "<span className="font-medium">{query}</span>"</p>
                  <p className="text-xs text-stone-400 mt-1">Coba kata kunci lain atau ubah filter kategori</p>
                </div>
              ) : (
                <div>
                  {Object.entries(grouped).map(([type, items]) => {
                    const config = TYPE_CONFIG[type] || {};
                    const Icon = config.icon || Search;
                    return (
                      <div key={type}>
                        <div className="px-5 py-2 bg-stone-50 border-b border-stone-100 flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${config.color || 'bg-stone-100 text-stone-600'}`}>
                            <Icon size={11} />
                            {config.label || type}
                          </span>
                          <span className="text-[11px] text-stone-400">{items.length} hasil</span>
                        </div>
                        {items.map((result, i) => {
                          flatIdx++;
                          const thisIdx = flatIdx;
                          const isSelected = thisIdx === selectedIdx;
                          return (
                            <button
                              key={`${type}-${i}`}
                              onClick={() => handleSelect(result)}
                              className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors border-b border-stone-50 ${
                                isSelected ? 'bg-blue-50' : 'hover:bg-stone-50'
                              }`}
                              data-testid={`search-result-${thisIdx}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-stone-800 truncate">{result.title}</p>
                                <p className="text-xs text-stone-400 truncate mt-0.5">{result.subtitle}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                {result.amount > 0 && (
                                  <p className="text-sm font-semibold text-stone-700">{formatCurrency(result.amount)}</p>
                                )}
                                <p className="text-[10px] text-stone-400">{result.date}</p>
                              </div>
                              {result.status && result.status !== '-' && result.status !== '' && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${statusColor(result.status)}`}>
                                  {result.status === 'paid' ? 'Lunas' :
                                   result.status === 'unpaid' ? 'Belum Bayar' :
                                   result.status === 'partial' ? 'Sebagian' :
                                   result.status === 'collected' ? 'Tertagih' :
                                   result.status === 'uncollected' ? 'Belum Tagih' :
                                   result.status === 'active' ? 'Aktif' :
                                   result.status === 'in' ? 'Masuk' :
                                   result.status === 'out' ? 'Keluar' :
                                   result.status}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {results.length > 0 && (
              <div className="px-5 py-2.5 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
                <span className="text-xs text-stone-400">{results.length} hasil ditemukan</span>
                <div className="flex items-center gap-3 text-[10px] text-stone-400">
                  <span><kbd className="px-1 py-0.5 bg-white rounded border border-stone-200 text-stone-500">↑↓</kbd> navigasi</span>
                  <span><kbd className="px-1 py-0.5 bg-white rounded border border-stone-200 text-stone-500">Enter</kbd> lihat detail</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
